import { addDays, addHours, isSameDay, millisecondsToMinutes } from "date-fns";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import type { Event } from "@/api/bale.zod";
import type { Coloring } from "@/coloring";

import { Tooltip } from "@/components/Tooltip";
import { useCurrentTime } from "@/hooks/useCurrentTime";

import { EVENT_COLORS } from "./EventColors";

export function Calendar({
  day,
  events,
  coloring,
  onSelectEvent,
}: {
  day: Date;
  events: Event[];
  coloring: Coloring<Event>;
  onSelectEvent?: (event: Event) => void;
}) {
  const { t } = useTranslation();
  const now = useCurrentTime("@minutely");
  const minTime = new Date(Math.max(day.getTime(), Math.min(...events.map((event) => event.start_time!.getTime()))));
  const maxTime = new Date(
    Math.min(addDays(day, 1).getTime(), Math.max(0, ...events.map((event) => (event.end_time ?? now).getTime()))),
  );

  const minHour = minTime.getHours();
  const maxHour = Math.min(24, Math.max(minHour, maxTime.getHours() || 24) + 1);

  return (
    <div className="grid grid-cols-[auto_1fr]">
      <div className="col-span-full row-1 grid grid-cols-subgrid pb-8">
        {Array.from({ length: maxHour - minHour + 1 }).map((_, i) => (
          // oxlint-disable-next-line no-array-index-key
          <div className="col-span-full grid h-8 grid-cols-subgrid items-end border-b border-divider" key={i}>
            <div className="col-1 font-hint">
              {minHour + i >= 0 && minHour + i <= 24 && t("hour", { date: addHours(day, minHour + i) })}
            </div>
          </div>
        ))}
      </div>
      <div className="col-2 row-1 flex flex-row gap-4 px-2">
        {events.map((event) => (
          <CalendarEvent
            key={event.id}
            day={day}
            minTime={minTime}
            maxTime={maxTime}
            event={event}
            onClick={() => onSelectEvent?.(event)}
            color={EVENT_COLORS[coloring(event)! % EVENT_COLORS.length]}
          />
        ))}
      </div>
      {isSameDay(now, day) && (
        <div
          className="col-span-full row-1 flex h-min flex-row items-center"
          style={{
            transform: `translateY(calc(${(millisecondsToMinutes(now.getTime() - addHours(day, minHour - 1).getTime()) * 2) / 60}rem - 50%))`,
          }}
        >
          <div className="grow border-b-2 border-water" />
          <div className="size-3 rounded-full border-2 border-water" />
        </div>
      )}
    </div>
  );
}

function CalendarEvent({
  day,
  minTime,
  maxTime,
  event,
  color,
  onClick,
}: {
  day: Date;
  minTime: Date;
  maxTime: Date;
  event: Event;
  color: string;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const now = useCurrentTime("@daily");
  const minHour = minTime.getHours();

  const start = event.start_time!.getTime() - addHours(day, minHour - 1).getTime();
  const startMinutes = start < 0 ? 0 : millisecondsToMinutes(start);

  const endTime = event.end_time ?? addHours(now, 1);
  const end = Math.min(endTime.getTime(), addHours(maxTime, 1).getTime()) - addHours(day, minHour - 1).getTime();
  const durationMinutes = millisecondsToMinutes(end - Math.max(start, 0));

  const anchorId = useId();

  function mouseEnter() {
    for (const element of document.querySelectorAll(`[data-component="CalendarEvent"][data-event-id="${event.id}"]`)) {
      element.classList.add("opacity-100!");
    }
  }
  function mouseLeave() {
    for (const element of document.querySelectorAll(`[data-component="CalendarEvent"][data-event-id="${event.id}"]`)) {
      element.classList.remove("opacity-100!");
    }
  }

  const goesToYesterday = !isSameDay(day, event.start_time!);
  const continuesLater = !event.end_time || !isSameDay(day, event.end_time);

  return (
    <Tooltip anchorId={anchorId} onShow={mouseEnter} onHide={mouseLeave} title={event.name || t("untitled-event")}>
      <div
        role="presentation"
        data-component="CalendarEvent"
        data-event-id={event.id}
        className="flex min-h-1 w-4 cursor-pointer flex-col gap-1 not-hover:opacity-80"
        style={{
          anchorName: `--${anchorId}`,
          marginTop: `${(2 * startMinutes) / 60}rem`,
          height: `${(2 * durationMinutes) / 60}rem`,
        }}
        onClick={onClick}
      >
        {goesToYesterday && (
          <>
            <div className="h-0.5 rounded-sm opacity-25" style={{ backgroundColor: color }} />
            <div className="h-1 rounded-sm opacity-50" style={{ backgroundColor: color }} />
            <div className="h-1.5 rounded-sm opacity-75" style={{ backgroundColor: color }} />
          </>
        )}
        <div className="grow basis-0 rounded-sm" style={{ backgroundColor: color }} />
        {continuesLater && (
          <>
            <div className="h-1.5 rounded-sm opacity-75" style={{ backgroundColor: color }} />
            <div className="h-1 rounded-sm opacity-50" style={{ backgroundColor: color }} />
            <div className="h-0.5 rounded-sm opacity-25" style={{ backgroundColor: color }} />
          </>
        )}
      </div>
    </Tooltip>
  );
}
